import classNames from 'classnames';
import {
  cloneElement,
  ReactElement,
  ReactNode,
  useMemo,
} from 'react';

interface MultiSelectProps {
  id: string;
  children: ReactElement<MultiSelectItemProps>[];
  title: string;
  onSelectionChange?: (selectedValues: string[]) => void;
  columns?: 1 | 2 | 3 | 4;
  value?: string[];
  dataTestId?: string;
}

interface MultiSelectItemProps {
  icon: ReactNode;
  label: string;
  value: string;
  isSelected?: boolean;
  onToggle?: () => void;
  highlight?: boolean;
 
}

const MultiSelect = ({
  id,
  children,
  title,
  onSelectionChange,
  columns = 1,
  value = [],
  dataTestId,
}: MultiSelectProps) => {

  const selectedItems = useMemo(() => new Set(value), [value]);

  const getGridClass = () => {
    const columnMap = {
      1: 'grid grid-cols-1',
      2: 'grid grid-cols-2',
      3: 'grid grid-cols-3',
      4: 'grid grid-cols-4',
    };

    return columnMap[columns] || 'flex flex-wrap';
  };

  const toggleItem = (val: string): void => {
    const next = new Set(selectedItems);
    next.has(val) ? next.delete(val) : next.add(val);
    onSelectionChange?.([...next]);
  };

  const allValues = useMemo(
    () => children.map((child) => child.props.value).filter(Boolean),
    [children],
  );

  const allSelected =
    allValues.length > 0 && allValues.every((val) => selectedItems.has(val));

  const toggleSelectAll = (): void => {
    const next = allSelected ? [] : [...allValues];
    onSelectionChange?.(next);
  };

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mt-sm mb-2xs">
        <p className="font-semibold text-gray-900 my-0" data-testid={dataTestId}>{title}</p>
        <button
          id={id}
          onClick={toggleSelectAll}
          className="text-purple-60 font-semibold text-h-sm hover:text-purple-80 transition-colors"
          data-testid={`registration-multi-bulk-select-${id}-${
            allSelected ? 'unselect-all' : 'select-all'
          }`}
        >
          {allSelected ? 'Unselect all' : 'Select all'}
        </button>
      </div>

      <div className={`${getGridClass()} gap-2xs w-full`}>
        {children.map((child, index) =>
          cloneElement(child, {
            key:        child.props.value || index,
            isSelected: selectedItems.has(child.props.value),
            onToggle:   () => toggleItem(child.props.value),
          }),
        )}
      </div>
    </div>
  );
};

const MultiSelectItem = ({
  icon,
  label,
  isSelected,
  onToggle,
  highlight,
  value,
}: MultiSelectItemProps) => (
  <button
    onClick={onToggle}
    data-testid={`registration-multi-select-item-${value}`}
    className={`
    flex items-center py-sm px-sm rounded-lg
    transition-all duration-200 ease-in-out
    flex-1 min-w-0
    ${isSelected ? 'bg-purple-0 text-purple-80' : 'bg-grey-0 text-grey-90 hover:bg-gray-200'}
    ${highlight ? '[--ring-color:76_0_194] animate-ring-pulse' : ''}
  `}
  >
    <span
      className={`mr-2xs ${!isSelected ? 'text-grey-50' : 'text-purple-80'}`}
    >
      {icon}
    </span>
    <span
      className={`flex-1 text-left text-h-sm  ${
        isSelected ? 'text-purple-80' : 'text-grey-90'
      }`}
    >
      {label}
    </span>
    <svg
      className={classNames('w-md h-md text-purple-900 flex-shrink-0', {
        'stroke-white':     !isSelected,
        'stroke-purple-80': isSelected,
      })}
      viewBox="0 0 24 24"
      fill="none"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  </button>
);

MultiSelect.Item = MultiSelectItem;

export { MultiSelect, MultiSelectItem };
export type { MultiSelectProps, MultiSelectItemProps };
